import { Box, type SxProps, type Theme, Tooltip, Typography } from '@mui/material'
import get from 'lodash/get'
import { useTranslate } from 'ra-core'
import { createElement, type ElementType } from 'react'
import { type RaRecord, useRecordContext, useResourceContext } from 'react-admin'

type EnumIconMap = Record<string, ElementType>

interface AdvancedEnumTextFieldProps {
  source: string
  className?: string
  fallback?: string
  icons?: EnumIconMap
  record?: RaRecord
  sx?: SxProps<Theme>
  tooltipPlacement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top'
  translateArgs?: Record<string, unknown>
}

const AdvancedEnumTextField = ({
  className,
  fallback = 'n/a',
  icons,
  source,
  sx,
  tooltipPlacement = 'left',
  translateArgs = {},
  ...props
}: AdvancedEnumTextFieldProps) => {
  const record = useRecordContext(props)
  const resource = useResourceContext()
  const translate = useTranslate()

  const rawValue = get(record, source)
  const normalizedValue = rawValue == null ? null : String(rawValue)
  const translationKey = normalizedValue
    ? `resources.${resource}.enums.${source}.values.${normalizedValue.toLowerCase()}`
    : fallback
  const translatedValue = translate(translationKey, { _: fallback, ...translateArgs })
  const IconComponent = normalizedValue && icons ? icons[normalizedValue] : undefined

  return IconComponent ? (
    <Box display="flex" alignItems="center" className={className} sx={sx}>
      <Tooltip title={translatedValue} placement={tooltipPlacement}>
        <span>{createElement(IconComponent)}</span>
      </Tooltip>
    </Box>
  ) : (
    <Typography component="span" variant="body2" className={className} sx={sx}>
      {translatedValue}
    </Typography>
  )
}

export default AdvancedEnumTextField
